import { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/utils/Modal';
import Select, { type StylesConfig } from 'react-select';
import type { ConversationDetail } from '@/types/conversation';
import type { Dashboard } from '@/types/dashboard';
import { dashboardService } from '@/services/dashboardService';
import { getSideMenuLocal, getPersonId } from '@/components/utils/CorgeeHelper';
import { dashboardApi } from '@/services/dashboardApi';
import { menuService } from '@/services/menuService';
import { dashboardStorage } from '@/lib';
import { conversationService } from '@/services/conversationService';
import type { Conversation } from '@/components/Globals/CorgeeStorageHelper';
import { dashboardGroupsService } from '@/components/DashboardGroups';
import { ToastContext } from '@/components/ui/Toast';
import { socketService } from '@/services/socketService';
import { conversationBus } from '@/lib/conversationBus';
import { removeConversationsFromDashboardCache } from '@/components/DashboardGroups/dashboardCache';

const getComboBoxStyles = (isDark: boolean): StylesConfig<any, false> => ({
  control: (base, state) => ({
    ...base,
    minHeight: '32px',
    height: '32px',
    borderRadius: '2px',
    boxShadow: 'none',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    backgroundColor: isDark ? '#3a3c3c' : base.backgroundColor,
    borderColor: state.isFocused ? '#66afe9' : isDark ? '#4a4c4c' : '#ccc',
    '&:hover': { borderColor: '#66afe9' },
  }),
  valueContainer: base => ({
    ...base,
    height: '32px',
    padding: '0 8px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    lineHeight: 'normal',
    color: isDark ? '#a9a093' : base.color,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({
    ...base,
    padding: '4px 6px',
    color: isDark ? '#6e6860' : base.color,
  }),
  clearIndicator: base => ({ ...base, padding: '4px', color: isDark ? '#6e6860' : base.color }),
  placeholder: base => ({
    ...base,
    fontSize: '12px',
    lineHeight: 'normal',
    color: isDark ? '#6e6860' : '#aaa',
  }),
  singleValue: base => ({
    ...base,
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: 'normal',
    color: isDark ? '#a9a093' : 'rgb(158, 149, 135)',
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 1000200,
  }),
  menu: base => ({
    ...base,
    marginTop: 0,
    borderRadius: '2px',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    backgroundColor: isDark ? '#3a3c3c' : '#fff',
    borderColor: isDark ? '#4a4c4c' : base.borderColor,
  }),
  menuList: base => ({
    ...base,
    padding: 0,
    maxHeight: '200px',
  }),
  option: (_base, state) => ({
    display: 'block',
    boxSizing: 'border-box',
    width: '100%',
    fontSize: '12px',
    fontFamily: 'Roboto, Arial, sans-serif',
    lineHeight: '1.4',
    padding: '6px 10px',
    backgroundColor: state.isFocused ? '#62accb' : isDark ? '#3a3c3c' : '#fff',
    color: state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
});

type moveItem = {
  Value: number;
  Text: string | undefined;
  Type: string;
};
type moveTo = {
  currentMoveTo: moveItem | null;
  options: moveItem[];
  // loading: boolean;
};

type dashboardItem = {
  Value: number;
  Text: string;
  DashboardGroups: any[];
  ConfigValueId: number;
};

type dashboardTo = {
  dashboardMoveTo: dashboardItem | null;
  options: dashboardItem[];
};

type columnItem = {
  Value: number;
  Text?: string;
  Name?: string;
  DashboardGroupId?: number;
  [key: string]: any;
};

type MoveCopyToModalProps = {
  open: boolean;
  title: string;
  setOpen: () => void;
  conversations?: ConversationDetail[];
  SocketConnectionId?: string;
  isMultiple: boolean;
  isInfowindowViewCard: boolean;
  moveFromColumn: (
    e: React.MouseEvent,
    conversation: Conversation,
    newGroup: number,
    response: Dashboard
  ) => void;
  removeFromColumn: (e: React.MouseEvent, conversation: Conversation) => void | Promise<void>;
  addToColumn?: (conversation: ConversationDetail, targetGroupId: number) => void;
  bulkRemoveFromColumnUi?: (conversationIds: number[]) => void;
};

const MoveCopyToModal = ({
  open,
  title,
  setOpen,
  conversations,
  SocketConnectionId,
  isMultiple,
  isInfowindowViewCard,
  moveFromColumn,
  removeFromColumn,
  bulkRemoveFromColumnUi,
}: MoveCopyToModalProps) =>
{
  const getConfigValue = () =>
  {
    const path = window.location.pathname;
    if (path.includes('dashboard'))
    {
      return parseInt(path.split('/').pop()!);
    }
    else return 1;
  };

  function getConfigValueName(configValueId: number): string | undefined
  {
    const sideMenus = getSideMenuLocal();
    if (!sideMenus) return undefined;
    for (let i = 0; i < (sideMenus?.length ?? 0); i++)
    {
      if (sideMenus![i]?.ConfigValueID == configValueId) return sideMenus![i]?.Text;
    }
    return undefined;
  }

  const isProjectView = window.location.href.indexOf('ProjectDetails') > -1;
  const currentConfigValue = getConfigValue();
  const isDetailView = window.location.href.includes('ConversationDetails');

  let SelectedDashboard: dashboardItem | null | undefined;
  let _SelectedMoveTo: moveItem | Record<string, unknown>;
  void _SelectedMoveTo!;
  let _SelectedGroup: any;
  void _SelectedGroup;
  let DashboardGroups: any[];
  void DashboardGroups!;
  let _itemsCount: number;
  void _itemsCount!;

  const toastCtx = useContext(ToastContext);

  const notify = (count: number, action: 'copied' | 'moved') =>
  {
    const msg = `${count} item${count !== 1 ? 's' : ''} ${action}`;
    const el = document.getElementById('items-moved-copied-count');
    if (el)
    {
      el.textContent = msg;
      el.style.display = 'block';
      setTimeout(() => { el.style.display = 'none'; }, 3000);
    }
    else if (toastCtx)
    {
      toastCtx.success(msg);
    }
  };

  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = getComboBoxStyles(isDark);

  const [loading, setLoading] = useState(false);
  const [moveTo, setMoveTo] = useState<moveTo>();
  const [dashboardsMoveTo, setDashboardsMoveTo] = useState<dashboardTo>();
  const [columns, setColumns] = useState<columnItem[]>([]);
  const [columnSelected, setColumnSelected] = useState<columnItem>();
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const pendingCopyFnRef = useRef<(() => Promise<void>) | null>(null);

  const [warring, _setWarring] = useState<boolean>();

  const conversation = conversations?.[0];

  // "Create a New Dashboard" / "Create New Column" placeholders both use Value 0
  const isNewDashboardSelected = dashboardsMoveTo?.dashboardMoveTo?.Value === 0;
  const isNewColumnSelected =
    !isNewDashboardSelected && columnSelected?.Value === 0 && !columnSelected?.DashboardGroupId;

  const close = () =>
  {
    setOpen();
    setLoading(false);
    setDuplicateWarning(false);
    pendingCopyFnRef.current = null;
  };

  useEffect(() =>
  {
    // Load side menu items if missing (standalone routes and Chat have no Sidebar)
    const ensureSideMenu = async () =>
    {
      const sideMenus = getSideMenuLocal();
      if (!sideMenus || sideMenus.length === 0)
      {
        await menuService.getMenuByPerson().catch(() => {});
      }
      if (!loading) initial();
    };
    ensureSideMenu();
  }, []);

  useEffect(() =>
  {
    onChange(1);
  }, [dashboardsMoveTo?.dashboardMoveTo]);

  const fetchGetDashboards = async (
    configValueId: number,
    ProjectId: number,
    organizeDashboard?: boolean,
  ): Promise<any[]> =>
  {
    setLoading(true);
    try
    {
      const response = await dashboardService.getDashboardsByConfigValue(
        configValueId,
        ProjectId || undefined,
        organizeDashboard || false,
      );
      return response ?? [];
    }
    catch (error)
    {
      console.error('Error loading organize dashboards:', error);
      return [];
    }
    finally
    {
      setLoading(false);
    }
  };

  const handleChangeMoveTo = (selectedOption: moveItem | null) =>
  {
    if (selectedOption)
    {
      setMoveTo({
        currentMoveTo: selectedOption,
        options: moveTo?.options ?? [],
      });
      onChange(0, selectedOption);
    }
    else
    {
      setMoveTo({ currentMoveTo: null, options: moveTo?.options ?? [] });
      setDashboardsMoveTo({ dashboardMoveTo: null, options: [] });
      setColumns([]);
      setColumnSelected(undefined);
    }
  };

  const handleChangeDashboardTo = (selectedOption: dashboardItem | null) =>
  {
    if (selectedOption)
    {
      setDashboardsMoveTo({
        dashboardMoveTo: selectedOption,
        options: dashboardsMoveTo?.options ?? [],
      });
      onChange(1);
    }
    else
    {
      setDashboardsMoveTo({
        dashboardMoveTo: null,
        options: dashboardsMoveTo?.options ?? [],
      });
      setColumns([]);
      setColumnSelected(undefined);
    }
  };

  const handleChangeColumnTo = (selectedOption: columnItem | null) =>
  {
    setColumnSelected(selectedOption ?? undefined);
  };

  const onDeleteKey =
    (clearFn: () => void) => (e: React.KeyboardEvent) =>
    {
      if (e.key === 'Delete') clearFn();
    };

  const initial = () =>
  {
    const moveToItems = [];
    let selectedMoveTo: any = {};

    if (isMultiple)
    {
      const firstConv = conversations?.[0];
      if (firstConv?.ProjectId && firstConv?.ProjectName)
      {
        moveToItems.push({
          Value: firstConv.ProjectId,
          Text: firstConv.ProjectName,
          Type: 'PROJECT',
        });
      }
      const seenIds: number[] = [];
      conversations?.forEach(c =>
      {
        if (seenIds.indexOf(c.ConfigValueId) < 0)
        {
          seenIds.push(c.ConfigValueId);
          moveToItems.push({ Value: c.ConfigValueId, Text: c.SideMenuText, Type: 'CONFIGVALUE' });
        }
      });
    }
    else
    {
      if (
        (conversation?.ProjectId && conversation?.ProjectName) ||
        (conversation?.MilestoneId && conversation?.ProjectName)
      )
      {
        moveToItems.push({
          Value: conversation?.ProjectId ? conversation?.ProjectId : conversation?.MilestoneId,
          Text: conversation?.ProjectName,
          Type: 'PROJECT',
        });
      }
      moveToItems.push({
        Value: conversation?.ConfigValueId,
        Text: conversation?.SideMenuText,
        Type: 'CONFIGVALUE',
      });
    }

    if (isProjectView)
    {
      if (isMultiple)
        selectedMoveTo = {
          Value: conversations?.[0]?.ProjectId,
          Text: conversations?.[0]?.ProjectName,
          Type: 'PROJECT',
        };
      else
        selectedMoveTo = {
          Value: conversation?.ProjectId,
          Text: conversation?.ProjectName,
          Type: 'PROJECT',
        };
    }
    else if (isDetailView)
    {
      SelectedDashboard = null;
      _SelectedMoveTo = {};
      _SelectedGroup = null;
      selectedMoveTo = null;
    }
    else
    {
      _SelectedMoveTo = {
        Value: currentConfigValue,
        Text: getConfigValueName(currentConfigValue),
        Type: 'CONFIGVALUE',
      };
      selectedMoveTo = _SelectedMoveTo as any;
    }
    if (conversation?.ConfigValueId != 1)
    {
      moveToItems.push({ Value: 1, Text: getConfigValueName(1), Type: 'CONFIGVALUE' });
    }
    // START THIS IS FOR GTD PHASE 1
    moveToItems.push({ Value: 1, Text: 'Organize', Type: 'ORGANIZE' });

    setMoveTo({ currentMoveTo: selectedMoveTo as moveItem, options: moveToItems as moveItem[] });

    onChange(0, selectedMoveTo);
  };

  const onChange = async (level: number, selectedItem?: any) =>
  {
    let response: any[] = [];
    let dashboard: any;
    const dashboardsToPush: dashboardItem[] = [];
    switch (level)
    {
      case 0: // "Move to" input
        SelectedDashboard = null;
        DashboardGroups = [];
        _SelectedGroup = null;
        if (!selectedItem) return;

        let currentDashboardId: any = 0;
        if (!isDetailView)
        {
          currentDashboardId = conversation?.DashboardGroupId;
        }
        else
        {
          currentDashboardId = null;
          SelectedDashboard = null;
        }
        _itemsCount = 0;

        switch (selectedItem.Type)
        {
          case 'PROJECT':
            response = await fetchGetDashboards(1, selectedItem.Value);
            // get DashboardId
            dashboard = response
              .flatMap(x => x.DashboardGroups)
              .find(x => x.DashboardGroupId == currentDashboardId);

            for (let i = 0; i < response.length; i++)
            {
              for (let j = 0; j < (moveTo?.options?.length ?? 0); j++)
              {
                const moveToElement = moveTo?.options?.[j];
                if (
                  moveToElement &&
                  (moveToElement as any).Type == 'CONFIGVALUE' &&
                  moveToElement.Value != 1
                )
                {
                  if (
                    response[i].ConfigValueId == moveToElement.Value &&
                    response[i].IsDefault &&
                    response[i].FakeDashboardType == 0
                  )
                  {
                    dashboardsToPush.push({
                      Value: response[i].DashboardId,
                      Text: response[i].Name,
                      DashboardGroups: response[i].DashboardGroups,
                      ConfigValueId: response[i].ConfigValueId,
                    });
                  }
                }
              }
              if (response[i].ConfigValueId == 1 && response[i].FakeDashboardType == 0)
              {
                dashboardsToPush.push({
                  Value: response[i].DashboardId,
                  Text: response[i].Name,
                  DashboardGroups: response[i].DashboardGroups,
                  ConfigValueId: response[i].ConfigValueId,
                });
              }

              if (dashboard?.DashboardId == response[i].DashboardId)
              {
                SelectedDashboard = {
                  Value: response[i].DashboardId,
                  Text: response[i].Name,
                  DashboardGroups: response[i].DashboardGroups,
                  ConfigValueId: response[i].ConfigValueId,
                };
              }
            }
            if (!SelectedDashboard && dashboardsToPush.length > 0)
            {
              SelectedDashboard = dashboardsToPush[0];
            }
            dashboardsToPush.push({
              Value: 0,
              Text: 'Create a New Dashboard',
              DashboardGroups: [],
              ConfigValueId: 1,
            });
            break;
          case 'CONFIGVALUE':
            response = await fetchGetDashboards(selectedItem.Value, -1);
            dashboard = (response as any[])
              .flatMap((x: any) => x.DashboardGroups)
              .find((x: any) => x.DashboardGroupId == currentDashboardId);

            for (var i = 0; i < response.length; i++)
            {
              if (
                response[i].ConfigValueId &&
                (response[i].ConfigValueId == 1 ||
                  response[i].ConfigValueId ==
                    (conversation?.ConfigValueId || selectedItem.Value)) &&
                response[i].FakeDashboardType == 0
              )
              {
                dashboardsToPush.push({
                  Value: response[i].DashboardId,
                  Text: response[i].Name,
                  DashboardGroups: response[i].DashboardGroups,
                  ConfigValueId: response[i].ConfigValueId,
                });
              }
              SelectedDashboard = {
                Value: response[i].DashboardId,
                Text: response[i].Name,
                DashboardGroups: response[i].DashboardGroups,
                ConfigValueId: response[i].ConfigValueId,
              };
            }
            dashboardsToPush.push({
              Value: 0,
              Text: 'Create a New Dashboard',
              DashboardGroups: [],
              ConfigValueId: 1,
            });
            break;
          case 'ORGANIZE':
            response = await fetchGetDashboards(selectedItem.Value, -1, true);
            // dashboardId = response.flatMap(x=> x.DashboardGroups).find(x=> x.DashboardGroupId == currentDashboardId);
            for (var i = 0; i < response.length; i++)
            {
              if (
                response[i].ConfigValueId &&
                (response[i].ConfigValueId == 1 ||
                  response[i].ConfigValueId ==
                    (conversation?.ConfigValueId || selectedItem.Value)) &&
                response[i].FakeDashboardType == 4
              )
              {
                dashboardsToPush.push({
                  Value: response[i].DashboardId,
                  Text: response[i].Name,
                  DashboardGroups: response[i].DashboardGroups,
                  ConfigValueId: response[i].ConfigValueId,
                });
              }
              // if (currentDashboardId === response[i].DashboardId) {
              SelectedDashboard = {
                Value: response[i].DashboardId,
                Text: response[i].Name,
                DashboardGroups: response[i].DashboardGroups,
                ConfigValueId: response[i].ConfigValueId,
              };
              // };
            }
            dashboardsToPush.push({
              Value: 0,
              Text: 'Create a New Dashboard',
              DashboardGroups: [],
              ConfigValueId: 1,
            });
            break;
          default:
            break;
        }
        setDashboardsMoveTo({
          options: dashboardsToPush,
          dashboardMoveTo: SelectedDashboard ?? null,
        });

        break;
      case 1: // dashbaord
        DashboardGroups = [];
        _SelectedGroup = null;
        // "Create a New Dashboard": its column is created on submit, so the dropdown stays disabled
        if (dashboardsMoveTo?.dashboardMoveTo?.Value === 0)
        {
          setColumns([]);
          setColumnSelected({ Value: 0, Text: 'Create New Column' });
          return;
        }
        if (!dashboardsMoveTo?.dashboardMoveTo?.Value) return;
        else
        {
          for (
            var i = 0;
            i < (dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups?.length ?? 0);
            i++
          )
          {
            if (!dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups[i].IsDefault)
            {
              DashboardGroups.push({
                Value: dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups[i].DashboardGroupId,
                Text: dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups[i].Name,
              });
            }
          }
        }
        // Copy before appending: mutating DashboardGroups duplicates the placeholder on every selection
        const newColumns = [...(dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups ?? [])].filter(
          c => c.DashboardGroupId,
        );
        newColumns.push({ Value: 0, Name: 'Create New Column' });
        setColumns(newColumns);
        setColumnSelected(undefined);
    }
  };

  // Legacy names the dashboard/column created from this modal, no extra inputs are shown
  const NEW_DASHBOARD_NAME = 'New Dashboard';
  const NEW_COLUMN_NAME = '(Double click to rename)';

  // Create a column (dashboard group) and return its id
  const createColumn = async (dashboardId: number, name: string, position: number) =>
  {
    const group = await dashboardGroupsService.createDashboardGroup({
      DashboardId: dashboardId,
      Conversations: [],
      Name: name,
      positionX: position * 3,
      col: position * 3,
      row: 0,
      sizeX: 3,
      sizeY: 0,
      isNew: true,
      TotalItems: 0,
      SocketConnectionId: socketService.connectionId || SocketConnectionId || '',
    });
    return group?.DashboardGroupId ?? 0;
  };

  // "Create a New Dashboard": create the dashboard + its first column, then send the item(s) there
  const itemToNewDashboard = async (e: React.MouseEvent) =>
  {
    const selectedMoveTo = moveTo?.currentMoveTo;
    if (!selectedMoveTo) return;

    setLoading(true);
    try
    {
      const isProject = selectedMoveTo.Type === 'PROJECT';
      const isOrganize = selectedMoveTo.Type === 'ORGANIZE';
      // Existing options minus the "Create a New Dashboard" placeholder
      const order = Math.max((dashboardsMoveTo?.options?.length ?? 1) - 1, 0);

      const newDashboard = await dashboardApi.create({
        DashboardId: 0,
        Name: NEW_DASHBOARD_NAME,
        IsPublic: false,
        Archived: false,
        PersonId: String(getPersonId()),
        ConfigValueId: isProject || isOrganize ? 1 : selectedMoveTo.Value,
        ProjectId: isProject ? selectedMoveTo.Value : null,
        GridSize: 7,
        DashboardViewTypeId: 1,
        Order: order,
        FakeDashboardType: isOrganize ? 4 : 0,
      });

      const newDashboardId = (newDashboard as any)?.DashboardId ?? 0;
      if (!newDashboardId)
      {
        toastCtx?.error?.('The dashboard could not be created');
        return;
      }

      // The backend already creates a default column with the new dashboard: reuse it
      // instead of adding a second one
      let newGroupId = (newDashboard as any)?.DashboardGroups?.[0]?.DashboardGroupId ?? 0;
      if (!newGroupId)
      {
        const created = await dashboardApi.getDashboardById(newDashboardId).catch(() => null);
        newGroupId = (created as any)?.DashboardGroups?.[0]?.DashboardGroupId ?? 0;
      }
      if (!newGroupId)
      {
        newGroupId = await createColumn(newDashboardId, NEW_COLUMN_NAME, 0);
      }

      await itemToOtherDashboardGroup(e, newDashboardId, newGroupId);

      // Make the new dashboard show up as a tab and select it
      conversationBus.emit('dashboard-created', {
        dashboardId: newDashboardId,
        configValueId: isProject || isOrganize ? 1 : selectedMoveTo.Value,
        projectId: isProject ? selectedMoveTo.Value : null,
      });
    }
    catch (error)
    {
      console.error('Error creating the new dashboard:', error);
      toastCtx?.error?.('The dashboard could not be created');
    }
    finally
    {
      setLoading(false);
    }
  };

  // "Create New Column" over an existing dashboard
  const itemToNewGroup = async (e: React.MouseEvent) =>
  {
    const dashboardId = dashboardsMoveTo?.dashboardMoveTo?.Value;
    if (!dashboardId) return;

    setLoading(true);
    try
    {
      const position = dashboardsMoveTo?.dashboardMoveTo?.DashboardGroups?.length ?? 0;
      const newGroupId = await createColumn(dashboardId, NEW_COLUMN_NAME, position);
      if (!newGroupId)
      {
        toastCtx?.error?.('The column could not be created');
        return;
      }
      await itemToOtherDashboardGroup(e, dashboardId, newGroupId);
    }
    catch (error)
    {
      console.error('Error creating the new column:', error);
      toastCtx?.error?.('The column could not be created');
    }
    finally
    {
      setLoading(false);
    }
  };

  const itemToOtherDashboardGroup = async (
    e: React.MouseEvent,
    overrideDashboardId?: number,
    overrideGroupId?: number,
  ) =>
  {
    const conv: ConversationDetail = conversation!;
    const newGroupId = overrideGroupId ?? columnSelected?.DashboardGroupId;
    const selectedDashboardId = overrideDashboardId ?? dashboardsMoveTo?.dashboardMoveTo?.Value;
    const curDash = conversation?.DashboardId ?? dashboardStorage.getCurrentDashboard()[0]?.dashboardId;
    const isCopy = title === 'Copy To';

    // Cards that left the dashboard: drop them from the source columns (removeFromColumn
    // may park them in the default column) and from the source dashboard's cached views
    const removeMovedFromSource = (ids: number[]) =>
    {
      bulkRemoveFromColumnUi?.(ids);
      removeConversationsFromDashboardCache(curDash, ids);
    };

    if (isInfowindowViewCard)
    {
      if (isCopy)
      {
        const liveSocketId = socketService.connectionId || SocketConnectionId || '';
        await dashboardGroupsService.insertDashboardGroupConversation({
          DashboardId: selectedDashboardId || 0,
          DashboardGroupId: newGroupId || 0,
          ConversationId: conv?.ConversationId || 0,
          ParentConversationId: conv?.ParentConversationId || 0,
          PreviousParentId: null,
          socketConnectionId: liveSocketId,
        });
        if (curDash === selectedDashboardId && newGroupId)
        {
          // Same-dashboard copy: behaves as a move in the UI (card changes column, no duplicate)
          conversationBus.emit('conversation-moved-to-column', {
            conversationId: conv.ConversationId,
            fromGroupId: conv.DashboardGroupId,
            toGroupId: newGroupId,
            conversation: conv,
          });
        }
        notify(1, 'copied');
        close();
      }
      else
      {
        await conversationService.Adddashboardgroupbyparameters(
          selectedDashboardId || 0,
          conv?.DashboardGroupId || 0,
          conv?.ConversationId || 0,
          newGroupId || 0,
          true,
          conv?.ParentConversationId || 0,
          SocketConnectionId,
        );
        if (conv.DashboardGroupId != null && conv.DashboardGroupId != undefined)
        {
          dashboardGroupsService.updatePercentDashboardGroupConversations({
            dashboardGroupConvs: [{ DashboardGroupId: conv.DashboardGroupId }],
            DashboardGroupIdDefault: 0,
          });
        }
        if (curDash === selectedDashboardId)
        {
          conversationBus.emit('conversation-moved-to-column', {
            conversationId: conv.ConversationId,
            fromGroupId: conv.DashboardGroupId,
            toGroupId: newGroupId ?? 0,
            conversation: conv,
          });
        }
        else
        {
          conversationBus.emit('conversation-deleted', { conversationId: conv.ConversationId });
          removeConversationsFromDashboardCache(curDash, [conv.ConversationId]);
        }
        notify(1, 'moved');
        close();
      }
      return;
    }
    else
    {
      if (isMultiple)
      {
        const convList = conversations ?? [];

        if (isCopy)
        {
          const conversationIds = convList.map(c => c.ConversationId).join(',');
          const bulkPayload = {
            DashboardId: selectedDashboardId || 0,
            DashboardGroupIdSend: newGroupId || 0,
            IncludeAllConversations: true,
            Conversations: convList.map(c => ({
              DashboardGroupId: c.DashboardGroupId || 0,
              ConversationId: c.ConversationId || 0,
              ParentConversationId: c.ParentConversationId || 0,
            })),
          };

          const executeBulkCopy = async () =>
          {
            await dashboardGroupsService.setConversationsAsViewed(conversationIds);

            if (curDash === selectedDashboardId && newGroupId)
            {
              // Same-dashboard copy: behaves as a move in the UI (card changes column, no duplicate).
              // insertDashboardGroupConversation moves the card at the backend level anyway.
              // Use the live socket ID so the broadcast is tagged as own-action and
              // useSocketRefreshButton discards it (prevents a stale refresh button appearing).
              const liveSocketId = socketService.connectionId || SocketConnectionId || '';
              for (const c of convList)
              {
                await dashboardGroupsService.insertDashboardGroupConversation({
                  DashboardId: selectedDashboardId || 0,
                  DashboardGroupId: newGroupId,
                  ConversationId: c.ConversationId,
                  ParentConversationId: c.ParentConversationId || 0,
                  PreviousParentId: null,
                  socketConnectionId: liveSocketId,
                });
                moveFromColumn(null as any, c as any, newGroupId, null as unknown as Dashboard);
              }
            }
            else
            {
              // Cross-dashboard: bulk endpoint
              await dashboardGroupsService.addDashboardGroupByParametersMultiple(bulkPayload);
            }

            notify(convList.length, 'copied');
            close();
          };

          const areDuplicates = await dashboardGroupsService.GetConversationsDuplicated(
            selectedDashboardId || 0,
            null,
            conversationIds,
          );

          if (areDuplicates)
          {
            pendingCopyFnRef.current = executeBulkCopy;
            setDuplicateWarning(true);
          }
          else
          {
            await executeBulkCopy();
          }
          return;
        }

        // Move: per-card loop
        for (const c of convList)
        {
          const response = await conversationService.Adddashboardgroupbyparameters(
            selectedDashboardId || 0,
            c.DashboardGroupId || 0,
            c.ConversationId || 0,
            newGroupId || 0,
            true,
            c.ParentConversationId || 0,
            SocketConnectionId,
          );
          if (c.DashboardGroupId != null)
          {
            dashboardGroupsService.updatePercentDashboardGroupConversations({
              dashboardGroupConvs: [{ DashboardGroupId: c.DashboardGroupId }],
              DashboardGroupIdDefault: 0,
            });
          }
          if (curDash === selectedDashboardId)
          {
            moveFromColumn(e, c as Conversation, newGroupId ?? 0, response as unknown as Dashboard);
          }
          else
          {
            await Promise.resolve(removeFromColumn(e, c as Conversation)).catch(console.error);
          }
        }
        if (curDash !== selectedDashboardId)
        {
          removeMovedFromSource(convList.map(c => c.ConversationId));
        }
        notify(convList.length, 'moved');
        close();
      }
      else
      {
        if (isCopy)
        {
          // Copy is always additive: source card stays, target gets the card too.
          // Use live socket ID to ensure the backend broadcast is tagged as own-action
          // so useSocketRefreshButton discards it and does not show a stale refresh button.
          const liveSocketId = socketService.connectionId || SocketConnectionId || '';
          await dashboardGroupsService.insertDashboardGroupConversation({
            DashboardId: selectedDashboardId || 0,
            DashboardGroupId: newGroupId || 0,
            ConversationId: conv?.ConversationId || 0,
            ParentConversationId: conv?.ParentConversationId || 0,
            PreviousParentId: null,
            socketConnectionId: liveSocketId,
          });
          // Same-dashboard copy: behaves as a move in the UI (card changes column, no duplicate)
          if (curDash === selectedDashboardId && newGroupId)
          {
            moveFromColumn(null as any, conv as any, newGroupId, null as unknown as Dashboard);
          }
          notify(1, 'copied');
        }
        else
        {
          // Move: card is removed from source and placed in target
          const response = await conversationService.Adddashboardgroupbyparameters(
            selectedDashboardId || 0,
            conv?.DashboardGroupId || 0,
            conv?.ConversationId || 0,
            newGroupId || 0,
            true,
            conv?.ParentConversationId || 0,
            SocketConnectionId,
          );
          if (conv.DashboardGroupId != null && conv.DashboardGroupId != undefined)
          {
            dashboardGroupsService.updatePercentDashboardGroupConversations({
              dashboardGroupConvs: [{ DashboardGroupId: conv.DashboardGroupId }],
              DashboardGroupIdDefault: 0,
            });
          }
          if (curDash === selectedDashboardId)
          {
            moveFromColumn(e, conv, newGroupId ?? 0, response as unknown as Dashboard);
          }
          else
          {
            await Promise.resolve(removeFromColumn(e, conv)).catch(console.error);
            removeMovedFromSource([conv.ConversationId]);
          }
          notify(1, 'moved');
        }
        close();
        // // TODO Steps after post the card
        // $.each(dataDashboardCopy, function (index, _conv) {
        //     var convElm = $("li[data-conversationId='" + _conv.ConversationId + "']").first();
        //     var newEvtType = ConversationActions.TreeViewChanged(_conv);

        //     if (newEvtType) {
        //         HandlerBindedFns.BindSingleFunction(convElm, 'conversationCard', newEvtType);
        //     }
        // });

        // ConversationActions.ChangeNoMoreItemsMessage(false, true);

        // if (sendToCtrl.SelectedDashboard.Value == curDash.DashboardId) {
        //     //Add one item to new dashboardgroup TotalItems
        //     var nDG = DashboardGroupContainer.GetGroupById(obj.DashboardGroupId);
        //     nDG.TotalItems = nDG.TotalItems + 1;
        // }
      }
      // // TODO Steps after post the card

      // var currentDashboardGroup = DashboardGroupContainer.GetGroupById(conv.DashboardGroupId);
      // var sameDashboard = curDash.DashboardId == selectedDashboardId;
      // var dashboardGroups = DashboardGroupContainer.GetCurrentDashboard().DashboardGroups;
      // var haveDefaultColumn = false;
      // for (var i = 0; i < dashboardGroups.length; i++) {
      //     if (dashboardGroups[i].IsDefault == true) {
      //         haveDefaultColumn = true;
      //     }
      // }
      // if (DashboardGroupContainer.GetDashboardViewTypeId() == 4 && sameDashboard == true) {
      //     for (var i = 0; i < dashboardGroups.length; i++) {
      //         if (!dashboardGroups[i].Conversations) {
      //             dashboardGroups[i].Conversations = [];
      //         };
      //         // Remove current conversation from other groups
      //         for (var j = dashboardGroups[i].Conversations.length - 1; j >= 0; j--) {
      //             if (dashboardGroups[i].Conversations[j].ConversationId == conv.ConversationId) {
      //                 DashboardGroupContainer.GetCurrentDashboard().DashboardGroups[i].Conversations.splice(j, 1);
      //                 // dashboardGroups[i].Conversations.splice(j, 1);
      //             };
      //         };

      //     };
      //     // Add conversation in the target group
      //     for (var i = 0; i < dashboardGroups.length; i++) {
      //         if (dashboardGroups[i].DashboardGroupId == newGroupId) {
      //             // dashboardGroups[i].Conversations.push(conv);
      //             DashboardGroupContainer.GetCurrentDashboard().DashboardGroups[i].Conversations.push(conv);

      //         }
      //     };

      //     ConversationActions.ChangeNoMoreItemsMessage(false, true);
      // }
      // else if (sameDashboard) {
      //     var curGroupElm = $('[data-dashboardgroupid=' + conv.DashboardGroupId + ']').find('ol.dashboardGroupSelector '),
      //         newGroupElm = $('[data-dashboardgroupid=' + newGroupId + ']').find('ol.dashboardGroupSelector '),
      //         lastGroupId = angular.copy(conv.DashboardGroupId),
      //         viewType = DashboardGroupContainer.GetDashboardViewTypeId(),
      //         dashboardTemplate = (viewType == 1) ? 'conversationTitle' : 'conversationCard';

      //     $("li[data-conversationId='" + conv.ConversationId + "']").remove();

      //     // Remove current conversation from other groups
      //     for (var i = 0; i < dashboardGroups.length; i++) {
      //         if (!dashboardGroups[i].Conversations) {
      //             dashboardGroups[i].Conversations = [];
      //         };
      //         for (var j = dashboardGroups[i].Conversations.length - 1; j >= 0; j--) {
      //             if (dashboardGroups[i].Conversations[j].ConversationId == conv.ConversationId) {
      //                 DashboardGroupContainer.GetCurrentDashboard().DashboardGroups[i].Conversations.splice(j, 1);
      //                 // dashboardGroups[i].Conversations.splice(j, 1);
      //             };
      //         };
      //     };
      //     conv.HasParent = false;
      //     conv.ParentConversationId = 0;
      //     conv.DashboardGroupId = newGroupId;
      //     // Add conversation in the target group
      //     for (var i = 0; i < dashboardGroups.length; i++) {
      //         if (dashboardGroups[i].DashboardGroupId == newGroupId) {
      //             // dashboardGroups[i].Conversations.push(conv);
      //             DashboardGroupContainer.GetCurrentDashboard().DashboardGroups[i].Conversations.push(conv);

      //         }
      //     };

      //     //ConversationsContainer.EditAttribute(convId, { 'DashboardGroupId': parseInt(newGroupId) });
      //     //DashboardGroupContainer.AddConvToGroup(newGroupId, conv, lastGroupId);

      //     Handlers.Render(dashboardTemplate, conv, newGroupElm, 'atTop');
      //     HandlerBindedFns.BindFunctions(dashboardTemplate, $("li[data-conversationId='" + conv.ConversationId + "']"));
      //     ConversationActions.ChangeNoMoreItemsMessage(false, true);
      // }
      // else if (DashboardGroupContainer.GetDashboardViewTypeId() == 4 && sameDashboard == false) {
      //     if (haveDefaultColumn == false) {
      //         $rootScope.$broadcast("refreshConversationsSpreadSheet", conv, true, e);
      //         ConversationService.DeleteDashboardGroupConversation(conv.DashboardGroupId, conv.ConversationId, 0).then(function (data) {
      //             $('li [data-conversationid="' + conv.ConversationId + '"]').remove();
      //             DashboardGroupContainer.RemoveConvToGroup(conv.DashboardGroupId, conv.ConversationId, undefined, undefined, undefined, "", SocketIOService.connectionId, undefined);
      //             ConversationActions.UpdateProgressTreeViews([conv.DashboardGroupId]);
      //             ConversationActions.ChangeNoMoreItemsMessage(false, true);
      //         });
      //         return;
      //     }
      //     else if (haveDefaultColumn == true) {
      //         $rootScope.$broadcast("refreshConversationsSpreadSheet", conv, undefined, e);
      //         ConversationService.DeleteDashboardGroupConversation(conv.DashboardGroupId, conv.ConversationId, 0).then(function (data) {
      //             $('li [data-conversationid="' + conv.ConversationId + '"]').remove();
      //             DashboardGroupContainer.RemoveConvToGroup(conv.DashboardGroupId, conv.ConversationId, undefined, undefined, undefined, "", SocketIOService.connectionId, undefined);
      //             ConversationActions.UpdateProgressTreeViews([conv.DashboardGroupId]);
      //             ConversationActions.ChangeNoMoreItemsMessage(false, true);
      //         });
      //     }
      // }
      // else if (DashboardGroupContainer.GetDashboardViewTypeId() == 8 && sameDashboard == false) {
      //     if (haveDefaultColumn == false) {
      //         $rootScope.$broadcast("refreshConversationsSpreadSheet", conv, true, e);
      //         ConversationService.DeleteDashboardGroupConversation(conv.DashboardGroupId, conv.ConversationId, 0).then(function (data) {
      //             $('li [data-conversationid="' + conv.ConversationId + '"]').remove();
      //             DashboardGroupContainer.RemoveConvToGroup(conv.DashboardGroupId, conv.ConversationId, undefined, undefined, undefined, "", SocketIOService.connectionId, undefined);
      //             ConversationActions.UpdateProgressTreeViews([conv.DashboardGroupId]);
      //             ConversationActions.ChangeNoMoreItemsMessage(false, true);
      //         });
      //         return;
      //     }
      //     else if (haveDefaultColumn == true) {
      //         $rootScope.$broadcast("refreshConversationsSpreadSheet", conv, undefined, e);
      //         ConversationService.DeleteDashboardGroupConversation(conv.DashboardGroupId, conv.ConversationId, 0).then(function (data) {
      //             $('li [data-conversationid="' + conv.ConversationId + '"]').remove();
      //             DashboardGroupContainer.RemoveConvToGroup(conv.DashboardGroupId, conv.ConversationId, undefined, undefined, undefined, "", SocketIOService.connectionId, undefined);
      //             ConversationActions.UpdateProgressTreeViews([conv.DashboardGroupId]);
      //             ConversationActions.ChangeNoMoreItemsMessage(false, true);
      //         });
      //     }
      // }
      // else if (sendToCtrl.currentCase == 3 && !currentDashboardGroup.IsDefault) {
      //     ConversationService.EnsureDashboardGroup(conv.DashboardGroupId, curDash.DashboardId, conv.ConversationId).then(function (foundDashboardboardGroupId) {
      //         ConversationService.DeleteDashboardGroupConversation(conv.DashboardGroupId ? conv.DashboardGroupId : foundDashboardboardGroupId, conv.ConversationId, 0).then(function (data) {
      //             $('li [data-conversationid="' + conv.ConversationId + '"]').remove();
      //             DashboardGroupContainer.RemoveConvToGroup(conv.DashboardGroupId, conv.ConversationId, undefined, undefined, undefined, "", SocketIOService.connectionId, undefined);
      //             ConversationActions.UpdateProgressTreeViews([conv.DashboardGroupId]);
      //             ConversationActions.ChangeNoMoreItemsMessage(false, true);
      //             if (DashboardGroupContainer.GetDashboardViewTypeId() == 10) $rootScope.$broadcast('filterChanged');
      //         });
      //     });
      // }

      // $rootScope.$broadcast('validateScrollBarToGetMoreConv', currentDashboardGroup.DashboardGroupId);

      // ConversationActions.MarkNotificationsAsViewed(obj.ConversationId);
    }
  };

  const submit = async (e: React.MouseEvent) =>
  {
    if (!moveTo?.currentMoveTo || submitting) return;

    setSubmitting(true);
    try
    {
      // "Create a New Dashboard": new dashboard + its first column
      if (isNewDashboardSelected)
      {
        await itemToNewDashboard(e);
      }
      // Existing dashboard, "Create New Column"
      else if (isNewColumnSelected)
      {
        await itemToNewGroup(e);
      }
      // Existing dashboard and existing column
      else
      {
        await itemToOtherDashboardGroup(e);
      }
    }
    catch (error)
    {
      console.error('Error submitting move/copy:', error);
    }
    finally
    {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => close()}
      title={duplicateWarning ? 'Duplicated Items' : title}
      preventCloseClickOut
    >
      {duplicateWarning ? (
        <div className="w-96">
          <div className="flex items-start py-2">
            <span className="text-sm dark:text-[#a9a093]">
              Some of the items you want to copy are already in that dashboard.
            </span>
          </div>
          <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
            <button
              type="button"
              disabled={loading}
              onClick={async () =>
              {
                if (pendingCopyFnRef.current)
                {
                  setLoading(true);
                  await pendingCopyFnRef.current();
                  setLoading(false);
                }
              }}
              className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1 dark:bg-[#3c6c80] dark:hover:brightness-[0.85] dark:border-[transparent]"
            >
              Copy anyway
            </button>
            <button
              type="button"
              onClick={() => close()}
              className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1 dark:bg-[#5c6060] dark:hover:brightness-[0.85] dark:border-[transparent]"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="w-96">
          <div className="gap-4">
            <div className="flex">
              <div className="w-24 flex-none text-sm align-middle leading-[45px] dark:text-[#a9a093]">
              Copy to:
              </div>
              <div className="flex-1">
                <Select
                // isDisabled={moveTo?.loading}
                  options={moveTo?.options}
                  value={moveTo?.currentMoveTo ?? null}
                  onChange={handleChangeMoveTo}
                  onKeyDown={onDeleteKey(() => handleChangeMoveTo(null))}
                  isSearchable={true}
                  isClearable={true}
                  getOptionLabel={(opt: moveItem) => opt.Text ?? ''}
                  className="w-full"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  placeholder="Select an item"
                />
              </div>
            </div>
            <div className="flex">
              <div className="w-24 flex-none text-sm align-middle leading-[45px] dark:text-[#a9a093]">
              Dashboards:
              </div>
              <div className="flex-1">
                <Select
                  isDisabled={!moveTo?.currentMoveTo}
                  onChange={handleChangeDashboardTo}
                  onKeyDown={onDeleteKey(() => handleChangeDashboardTo(null))}
                  value={dashboardsMoveTo?.options.find(
                    x => x.Value === dashboardsMoveTo?.dashboardMoveTo?.Value,
                  ) ?? null}
                  options={dashboardsMoveTo?.options}
                  isSearchable={true}
                  isClearable={true}
                  getOptionLabel={(opt: dashboardItem) => opt.Text}
                  className="w-full"
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  placeholder="Select a dashboard"
                />
              </div>
            </div>
            <div className="flex">
              <div className="w-24 flex-none text-sm align-middle leading-[45px] dark:text-[#a9a093]">
              Columns:
              </div>
              <div className="flex-1">
                <Select
                  isDisabled={!dashboardsMoveTo?.dashboardMoveTo || isNewDashboardSelected}
                  onChange={handleChangeColumnTo}
                  onKeyDown={onDeleteKey(() => handleChangeColumnTo(null))}
                  value={columnSelected ?? null}
                  options={columns}
                  isSearchable={true}
                  isClearable={true}
                  getOptionLabel={(opt: columnItem) => opt.Name ?? opt.Text ?? ''}
                  className="w-full"
                  styles={selectStyles}
                  placeholder="Select a column"
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                />
              </div>
            </div>
          </div>
          {warring && (
            <div
              style={{
                margin: '12px 0',
                padding: '4px 12px',
                backgroundColor: isDark ? '#1a3a3f' : '#dcf3f7',
                color: isDark ? '#62aacb' : 'black',
                fontSize: '12px',
                textAlign: 'center',
              }}
            >
            Warning: Items with different project or item type, will not be moved.
            </div>
          )}

          <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
            <button
              type="button"
              disabled={loading || submitting || !columnSelected}
              onClick={submit}
              className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1 disabled:opacity-60 disabled:cursor-not-allowed dark:bg-[#3c6c80] dark:hover:brightness-[0.85] dark:border-[transparent]"
            >
              {submitting ? (
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                'Submit'
              )}
            </button>
            <button
              type="button"
              onClick={() => close()}
              className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1 dark:bg-[#5c6060] dark:hover:brightness-[0.85] dark:border-[transparent]"
            >
            Cancel
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MoveCopyToModal;
